import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { AppContext } from 'context/app-context';
import { initSideModal, usersRoles, generateSlotHours, combineLocalDateAndTime } from 'helpers/utils';
import { getOrganizationOptions } from 'store/features/organizationSlice';
import { createSlotAction, getOrgParkings, IParkingOption } from 'store/features/slotsSlice';
import { setSideModalData } from 'store/features/modelSlice';
import Select, { IDefaultOption } from 'components/common/select';
import DatePicker from 'components/common/date-picker';
import CommonLayout from './common-layout';
import toast from 'react-hot-toast';
import moment from 'moment';

const TIME_OPTIONS: IDefaultOption[] = generateSlotHours().map(h => ({
  id: h,
  name: h,
  value: h,
}));

const AddEditSlot: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setLoader } = useContext(AppContext);
  const { sideModal } = useAppSelector(state => state.modal);
  const { selectedOrganization, user } = useAppSelector(state => state.auth);

  const { content } = sideModal;
  const { callBackFun } = content;

  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [startTime, setStartTime] = useState<IDefaultOption | null>(null);
  const [endTime, setEndTime] = useState<IDefaultOption | null>(null);
  const [repeat, setRepeat] = useState<'none' | 'weekly'>('none');
  const [workshopCapacity, setWorkshopCapacity] = useState<number>(1);

  // Org & parking selection
  const [orgOptions, setOrgOptions] = useState<IDefaultOption[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<IDefaultOption | null>(null);
  const [parkingOptions, setParkingOptions] = useState<IDefaultOption[]>([]);
  const [selectedParking, setSelectedParking] = useState<IDefaultOption | null>(null);

  const isSuperAdmin = selectedOrganization?.role_name === usersRoles?.SUPER_ADMIN;
  const isDealerAdmin = selectedOrganization?.role_name === usersRoles?.DEALER_ADMIN;
  const hasMultipleOrgs = (user?.login_details?.length || 0) > 1;
  const showOrgDropdown = isSuperAdmin || (isDealerAdmin && hasMultipleOrgs);

  // Fetch organisation options
  useEffect(() => {
    if (showOrgDropdown) {
      dispatch(getOrganizationOptions({ request: {} }))
        .then((data: any) => {
          const opts = (data || []).map((org: any) => ({
            id: org._id || org.id,
            _id: org._id || org.id,
            name: org.organization_name || org.name,
          }));
          setOrgOptions(opts);
          if (opts.length > 0) {
            setSelectedOrg(opts[0]);
          }
        })
        .catch(() => { });
    } else {
      // Single org — use the org from auth
      if (selectedOrganization?.organization_id) {
        const singleOrg: IDefaultOption = {
          id: selectedOrganization.organization_id,
          _id: selectedOrganization.organization_id,
          name: selectedOrganization.organization_name || '',
        };
        setSelectedOrg(singleOrg);
        setOrgOptions([singleOrg]);
      }
    }
  }, []);

  // Fetch parkings when org changes
  useEffect(() => {
    const orgId = selectedOrg?.id || selectedOrg?._id;
    if (!orgId) {
      setParkingOptions([]);
      setSelectedParking(null);
      return;
    }
    dispatch(getOrgParkings({ org_id: orgId }))
      .then((parkings: IParkingOption[]) => {
        const opts = parkings.map(p => ({
          id: p._id,
          _id: p._id,
          name: p.address_title || p.city || 'Parking',
        }));
        setParkingOptions(opts);
        if (opts.length === 1) {
          setSelectedParking(opts[0]);
        } else {
          setSelectedParking(null);
        }
      })
      .catch(() => {
        setParkingOptions([]);
        setSelectedParking(null);
      });
  }, [selectedOrg]);

  const handleCreate = async () => {
    // Validation
    const orgId = selectedOrg?.id || selectedOrg?._id;
    const parkingId = selectedParking?.id || selectedParking?._id;

    if (!orgId) {
      toast.error(t('workshopAppointment.slots.selectOrganisation'));
      return;
    }
    if (!parkingId) {
      toast.error(t('workshopAppointment.slots.selectParking'));
      return;
    }
    if (!date) {
      toast.error(t('workshopAppointment.slots.selectDate'));
      return;
    }
    if (!startTime) {
      toast.error(t('workshopAppointment.slots.selectStartTime'));
      return;
    }
    if (!endTime) {
      toast.error(t('workshopAppointment.slots.selectEndTime'));
      return;
    }
    if (startTime.value && endTime.value && startTime.value >= endTime.value) {
      toast.error(t('workshopAppointment.slots.endTimeError'));
      return;
    }
    if (!workshopCapacity || workshopCapacity < 1) {
      toast.error(t('workshopAppointment.slots.enterWorkshopCapacity'));
      return;
    }

    try {
      setLoader(true);
      await dispatch(
        createSlotAction({
          request: {
            slots: [
              {
                organization_id: orgId,
                parking_id: parkingId,
                start_time: combineLocalDateAndTime(date, startTime.value || ''),
                end_time: combineLocalDateAndTime(date, endTime.value || ''),
                workshop_capacity: workshopCapacity,
                repeat,
              },
            ],
          },
        }),
      );
      toast.success(t('workshopAppointment.slots.slotCreated'));
      if (callBackFun) callBackFun();
      dispatch(setSideModalData(initSideModal));
    } catch (_err) {
      // Error toast handled by API interceptor
    } finally {
      setLoader(false);
    }
  };

  return (
    <CommonLayout
      onConfirm={handleCreate}
      confirmButtonProps={{ label: t('workshopAppointment.slots.save') }}
      cancelButtonProps={{ label: t('workshopAppointment.slots.cancel') }}
    >
      <div className="flex flex-col gap-5 px-10 py-6">
        {/* Date */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-black2">
            {t('workshopAppointment.slots.date')}
          </label>
          <DatePicker
            selected={date}
            onChange={d => setDate(d ? moment(d).format('YYYY-MM-DD') : '')}
            isAllowPastDate={false}
          />
        </div>

        {/* Start Time */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-black2">
            {t('workshopAppointment.slots.startTime')}
          </label>
          <Select
            placeholder={t('workshopAppointment.slots.selectStartTime')}
            options={TIME_OPTIONS}
            selected={startTime}
            setSelected={setStartTime}
            isSearchable
            search=""
            setSearch={() => { }}
          />
        </div>

        {/* End Time */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-black2">
            {t('workshopAppointment.slots.endTime')}
          </label>
          <Select
            placeholder={t('workshopAppointment.slots.selectEndTime')}
            options={
              startTime?.value
                ? TIME_OPTIONS.filter(o => (o.value || '') > (startTime.value || ''))
                : TIME_OPTIONS
            }
            selected={endTime}
            setSelected={setEndTime}
            isSearchable
            search=""
            setSearch={() => { }}
          />
        </div>

        {/* Availability */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-black2">
            {t('workshopAppointment.slots.availability')}
          </label>
          <div className="flex items-center gap-4">
            <label
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2 border cursor-pointer transition-all ${repeat === 'none'
                  ? 'border-primary bg-grey4 text-primary font-medium'
                  : 'border-divider text-grey2 hover:border-grey5'
                }`}
            >
              <input
                type="radio"
                name="repeat"
                checked={repeat === 'none'}
                onChange={() => setRepeat('none')}
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm">{t('workshopAppointment.slots.noRepeat')}</span>
            </label>
            <label
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2 border cursor-pointer transition-all ${repeat === 'weekly'
                  ? 'border-primary bg-grey4 text-primary font-medium'
                  : 'border-divider text-grey2 hover:border-grey5'
                }`}
            >
              <input
                type="radio"
                name="repeat"
                checked={repeat === 'weekly'}
                onChange={() => setRepeat('weekly')}
                className="accent-primary w-4 h-4"
              />
              <span className="text-sm">{t('workshopAppointment.slots.repeatWeekly')}</span>
            </label>
          </div>
        </div>

        {/* Organisation dropdown */}
        {showOrgDropdown && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-black2">
              {t('workshopAppointment.slots.organisation')}
            </label>
            <Select
              placeholder={t('workshopAppointment.slots.selectOrganisation')}
              options={orgOptions}
              selected={selectedOrg}
              setSelected={setSelectedOrg}
              isSearchable
              search=""
              setSearch={() => { }}
            />
          </div>
        )}

        {/* Parking dropdown or details */}
        {parkingOptions.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-black2">
              {t('workshopAppointment.slots.parking')}
            </label>
            <Select
              placeholder={t('workshopAppointment.slots.selectParking')}
              options={parkingOptions}
              selected={selectedParking}
              setSelected={setSelectedParking}
            />
          </div>
        ) : parkingOptions.length === 1 ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-black2">
              {t('workshopAppointment.slots.parking')}
            </label>
            <div className="flex items-center px-4 h-11 rounded-1 border-1.5 border-grey bg-grey8 text-sm font-medium text-black2">
              {parkingOptions[0].name}
            </div>
          </div>
        ) : null}

        {/* Workshop capacity */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-black2">
            {t('workshopAppointment.slots.totalWorkshops')}
          </label>
          <input
            type="number"
            min={1}
            value={workshopCapacity}
            onChange={e => setWorkshopCapacity(Math.max(1, Number(e.target.value)))}
            className="w-full h-11 px-4 rounded-1 border-1.5 border-grey text-base font-medium text-black2 outline-none focus:border-active hover:border-active transition-colors bg-white"
            placeholder={t('workshopAppointment.slots.enterWorkshopCapacity')}
          />
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddEditSlot;
